import React, { useMemo, useState } from 'react';
import { buildClientDisplayName, SERVICE_CONTRACT_MODEL_OPTIONS } from '../serviceOptions';
import { resolveClosureStatusLabel, resolveServiceRecognitionBadge } from '../serviceBilling';

const formatCurrency = (value = 0) =>
  new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatShortDateTime = (value) => {
  if (!value) return 'Sin fecha';
  if (typeof value?.toDate === 'function') return formatShortDateTime(value.toDate());
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Sin fecha';
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
};

const contractModelLabels = SERVICE_CONTRACT_MODEL_OPTIONS.reduce((acc, item) => {
  acc[item.value] = item.label;
  return acc;
}, {});

const resolveContractModelLabel = (contract = {}) => {
  const raw =
    contract?.commercialModel ||
    contract?.contractModel ||
    contract?.pricingModel ||
    '';

  return contractModelLabels[String(raw || '').trim()] || contract?.contractType || 'Acuerdo comercial';
};

const startOfMonth = (date = new Date()) => new Date(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (date = new Date()) => new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999);

const toDate = (value) => {
  if (!value) return null;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const serviceDate = (service) =>
  toDate(service?.financialClosedAt) ||
  toDate(service?.revenueRecognizedAt) ||
  toDate(service?.completedAt) ||
  toDate(service?.paidAt) ||
  toDate(service?.invoicedAt) ||
  toDate(service?.scheduledDate) ||
  toDate(service?.date) ||
  toDate(service?.createdAt);

const currentMonthLabel = new Intl.DateTimeFormat('es-AR', {
  month: 'long',
  year: 'numeric',
}).format(new Date());

export default function ServiceClientCommercialView({
  clients = [],
  contracts = [],
  services = [],
  closures = [],
  onEditClient = null,
}) {
  const [search, setSearch] = useState('');
  const [expandedClientId, setExpandedClientId] = useState('');
  const [expandedSections, setExpandedSections] = useState({});

  const rows = useMemo(() => {
    const query = String(search || '').trim().toLowerCase();
    const monthStart = startOfMonth(new Date());
    const monthEnd = endOfMonth(new Date());
    const contractByClientId = new Map();
    const closuresByClientId = new Map();

    contracts.forEach((contract) => {
      const clientId = String(contract?.clientId || '').trim();
      if (!clientId || contractByClientId.has(clientId)) return;
      contractByClientId.set(clientId, contract);
    });

    closures.forEach((closure) => {
      const clientId = String(closure?.clientId || '').trim();
      if (!clientId) return;
      const list = closuresByClientId.get(clientId) || [];
      list.push(closure);
      closuresByClientId.set(clientId, list);
    });

    return clients
      .map((client) => {
        const clientId = String(client?.id || '').trim();
        const contract = contractByClientId.get(clientId) || null;
        const clientServices = services
          .filter((service) => String(service?.clientId || '').trim() === clientId)
          .sort((a, b) => (serviceDate(b)?.getTime() || 0) - (serviceDate(a)?.getTime() || 0));
        const clientClosures = (closuresByClientId.get(clientId) || []).sort(
          (a, b) => (toDate(b?.recognizedAt || b?.paidAt || b?.updatedAt)?.getTime() || 0) - (toDate(a?.recognizedAt || a?.paidAt || a?.updatedAt)?.getTime() || 0)
        );
        const monthServices = clientServices.filter((service) => {
          const date = serviceDate(service);
          return date && date >= monthStart && date <= monthEnd;
        });
        const variableRevenue = monthServices.reduce((acc, service) => acc + Math.max(0, Number(service?.recognizedSalePrice ?? service?.realizedSalePrice ?? service?.salePrice ?? 0)), 0);
        const totalOperationalCost = monthServices.reduce((acc, service) => acc + Math.max(0, Number(service?.realizedOperationalCost ?? service?.estimatedOperationalCost ?? 0)), 0);
        const totalMargin = monthServices.reduce((acc, service) => acc + Math.max(0, Number(service?.recognizedMargin ?? service?.realizedMargin ?? service?.estimatedMargin ?? 0)), 0);
        const monthlyFixed =
          contract?.commercialModel === 'monthly_fixed' && String(contract?.status || '').trim().toLowerCase() === 'active'
            ? Math.max(0, Number(contract?.monthlyFee || 0))
            : 0;
        const estimatedCommercialTotal = monthlyFixed > 0 ? monthlyFixed : variableRevenue;
        const lastActivityAt = clientServices.length > 0 ? serviceDate(clientServices[0]) : null;
        const normalizedStatus = String(contract?.status || '').trim().toLowerCase();

        return {
          client,
          contract,
          clientId,
          monthServices,
          clientClosures,
          serviceCount: monthServices.length,
          variableRevenue,
          totalOperationalCost,
          totalMargin,
          monthlyFixed,
          estimatedCommercialTotal,
          lastActivityAt,
          normalizedStatus,
        };
      })
      .filter((row) => {
        const name = buildClientDisplayName(row.client).toLowerCase();
        const phone = String(row.client?.phone || '').toLowerCase();
        const contact = String(row.client?.contacts?.[0]?.name || row.client?.contactName || '').toLowerCase();
        if (!query) return true;
        return name.includes(query) || phone.includes(query) || contact.includes(query);
      })
      .sort((a, b) => {
        const aTime = a.lastActivityAt?.getTime?.() || 0;
        const bTime = b.lastActivityAt?.getTime?.() || 0;
        if (bTime !== aTime) return bTime - aTime;
        return buildClientDisplayName(a.client).localeCompare(buildClientDisplayName(b.client), 'es');
      });
  }, [clients, closures, contracts, search, services]);

  const clientStats = useMemo(() => {
    let withContract = 0;
    let withoutContract = 0;
    rows.forEach((row) => {
      if (row.contract) withContract += 1;
      else withoutContract += 1;
    });
    return { withContract, withoutContract };
  }, [rows]);

  const visibleCount = rows.length;
  const isSectionOpen = (clientId, section) => Boolean(expandedSections?.[clientId]?.[section]);
  const toggleSection = (clientId, section) => {
    setExpandedSections((current) => ({
      ...current,
      [clientId]: {
        contract: section === 'contract' ? !current?.[clientId]?.contract : false,
        history: section === 'history' ? !current?.[clientId]?.history : false,
      },
    }));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-[30px] border border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_100%)] px-5 py-5 shadow-[0_14px_34px_rgba(15,23,42,0.06)]">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Clientes comerciales</p>
            <h3 className="mt-2 text-xl font-black tracking-tight text-slate-900">Base viva de clientes, contratos e historial</h3>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              Cada cliente queda resumido en una barra. Tocás y se despliega su acuerdo, el movimiento del período y el detalle de viajes sin repetir pantallas.
            </p>
          </div>

          <div className="grid min-w-[260px] grid-cols-2 gap-2">
            <div className="rounded-2xl border border-violet-200 bg-violet-50 px-3 py-2 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-700">Con contrato</p>
              <p className="mt-1 text-lg font-black tracking-tight text-violet-900">{clientStats.withContract}</p>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 shadow-sm">
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-amber-700">Sin contrato</p>
              <p className="mt-1 text-lg font-black tracking-tight text-amber-900">{clientStats.withoutContract}</p>
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 xl:grid-cols-[minmax(0,1fr)_auto] xl:items-end">
          <label className="block space-y-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Buscar cliente</span>
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Nombre, contacto o teléfono"
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition focus:border-cyan-300 focus:ring-4 focus:ring-cyan-100"
            />
          </label>
          <p className="text-xs text-slate-400 xl:pb-1">
            Mostrando {visibleCount} cliente{visibleCount === 1 ? '' : 's'} · historial de {currentMonthLabel}
          </p>
        </div>
      </div>

      {rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((row) => {
            const hasContract = Boolean(row.contract);
            const isActive = hasContract && row.normalizedStatus === 'active';
            const isExpanded = expandedClientId === row.clientId;
            const tone = !hasContract
              ? {
                  shell: 'border-slate-200 bg-[linear-gradient(135deg,#ffffff_0%,#f8fafc_52%,#f1f5f9_100%)]',
                  banner: 'bg-[linear-gradient(90deg,#cbd5e1_0%,#94a3b8_100%)]',
                  badge: 'border-slate-300 bg-slate-100 text-slate-700',
                  glow: 'bg-slate-200/60',
                  panel: 'border-slate-200 bg-slate-50 text-slate-700',
                  summary: 'Sin contrato',
                  support: 'Viaje puntual o cliente todavía sin acuerdo comercial.',
                }
              : isActive
                ? {
                    shell: 'border-violet-200 bg-[linear-gradient(135deg,#f5f3ff_0%,#ffffff_52%,#faf5ff_100%)]',
                    banner: 'bg-[linear-gradient(90deg,#7c3aed_0%,#22d3ee_100%)]',
                    badge: 'border-violet-300 bg-violet-100 text-violet-800',
                    glow: 'bg-violet-200/70',
                    panel: 'border-violet-200 bg-violet-50 text-violet-800',
                    summary: 'Contrato activo',
                    support: 'Ya trabaja con acuerdo comercial vigente.',
                  }
                : {
                    shell: 'border-amber-200 bg-[linear-gradient(135deg,#fffbeb_0%,#ffffff_52%,#fff7ed_100%)]',
                    banner: 'bg-[linear-gradient(90deg,#f59e0b_0%,#f97316_100%)]',
                    badge: 'border-amber-300 bg-amber-100 text-amber-800',
                    glow: 'bg-amber-200/70',
                    panel: 'border-amber-200 bg-amber-50 text-amber-800',
                    summary: row.normalizedStatus === 'paused' ? 'Contrato pausado' : row.normalizedStatus === 'closed' ? 'Contrato cerrado' : 'Contrato borrador',
                    support: 'Tiene acuerdo guardado, pero hoy no corre como activo.',
                  };
            const contractLabel = row.contract ? resolveContractModelLabel(row.contract) : 'Cliente puntual';
            const statusLabel = !hasContract
              ? 'Sin contrato'
              : isActive
                ? 'Activo'
                : row.normalizedStatus === 'paused'
                  ? 'Pausado'
                  : row.normalizedStatus === 'closed'
                    ? 'Cerrado'
                    : 'Borrador';

            return (
              <div key={row.clientId} className={`relative overflow-hidden rounded-[30px] border shadow-[0_18px_40px_rgba(15,23,42,0.09)] ${tone.shell}`}>
                <div className={`absolute inset-x-0 top-0 h-1.5 ${tone.banner}`} />
                <div className={`absolute -right-8 top-0 h-24 w-24 rounded-full blur-3xl ${tone.glow}`} />

                <button
                  type="button"
                  onClick={() => setExpandedClientId((current) => (current === row.clientId ? '' : row.clientId))}
                  className="flex w-full flex-wrap items-start justify-between gap-4 px-5 py-5 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.22em] shadow-sm ${tone.badge}`}>
                        {statusLabel}
                      </span>
                      <span className="rounded-full border border-white/80 bg-white/95 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-600">
                        {contractLabel}
                      </span>
                      <span className="rounded-full border border-cyan-200 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-700">
                        {row.serviceCount} viaje{row.serviceCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <p className="mt-3 text-xl font-black tracking-tight text-slate-950">{buildClientDisplayName(row.client)}</p>
                    <p className="mt-1 text-sm text-slate-500">{tone.support}</p>
                  </div>

                  <div className="grid min-w-[310px] grid-cols-2 gap-2 lg:grid-cols-4">
                    <div className="rounded-[18px] border border-white/85 bg-white/90 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Último viaje</p>
                      <p className="mt-1 text-sm font-black text-slate-900">{row.lastActivityAt ? formatShortDateTime(row.lastActivityAt) : 'Sin viajes'}</p>
                    </div>
                    <div className="rounded-[18px] border border-white/85 bg-white/90 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">Costo</p>
                      <p className="mt-1 text-sm font-black text-slate-900">{formatCurrency(row.totalOperationalCost)}</p>
                    </div>
                    <div className="rounded-[18px] border border-violet-200 bg-violet-50 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-violet-700">Diferencia</p>
                      <p className="mt-1 text-sm font-black text-violet-950">{formatCurrency(row.totalMargin)}</p>
                    </div>
                    <div className="rounded-[18px] border border-emerald-200 bg-emerald-50 px-3 py-2 shadow-sm">
                      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">Total estimado</p>
                      <p className="mt-1 text-sm font-black text-emerald-950">{formatCurrency(row.estimatedCommercialTotal)}</p>
                    </div>
                  </div>
                </button>

                {isExpanded ? (
                  <div className="border-t border-white/70 px-5 pb-5 pt-4">
                    <div className={`rounded-[24px] border px-4 py-4 shadow-sm ${tone.panel}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-[0.24em] opacity-80">Resumen del cliente</p>
                          <p className="mt-1 text-base font-black tracking-tight">{tone.summary}</p>
                        </div>
                        <span className={`rounded-full border px-3 py-1 text-[11px] font-black uppercase tracking-[0.2em] ${tone.badge}`}>
                          {statusLabel}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-6 opacity-90">{tone.support}</p>
                    </div>

                    <div className="mt-4 space-y-3">
                      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/90 shadow-sm">
                        <button
                          type="button"
                          onClick={() => toggleSection(row.clientId, 'contract')}
                          className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-4 text-left"
                        >
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Contrato</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">
                              {hasContract ? `${contractLabel} · ${statusLabel}` : 'Sin contrato cargado'}
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${tone.badge}`}>
                              {hasContract ? 'Ver acuerdo' : 'Configurar'}
                            </span>
                            <span className="text-slate-400">{isSectionOpen(row.clientId, 'contract') ? '−' : '+'}</span>
                          </div>
                        </button>

                        {isSectionOpen(row.clientId, 'contract') ? (
                          <div className="border-t border-slate-200 px-4 py-4">
                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Referencia</p>
                                <p className="mt-1 text-sm font-bold text-slate-900">{row.contract?.name || 'Sin nombre comercial'}</p>
                              </div>
                              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cobro</p>
                                <p className="mt-1 text-sm font-bold text-slate-900">{row.contract?.billingContactName || 'Sin contacto de cobro'}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  {[row.contract?.billingPhone, row.contract?.billingEmail].filter(Boolean).join(' · ') || 'Todavía sin datos'}
                                </p>
                              </div>
                              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Abono fijo</p>
                                <p className="mt-1 text-sm font-bold text-slate-900">{formatCurrency(row.monthlyFixed)}</p>
                              </div>
                              <div className="rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-3 shadow-sm">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Aviso previo</p>
                                <p className="mt-1 text-sm font-bold text-slate-900">
                                  {row.contract?.autoReminderEnabled ? `${Number(row.contract?.reminderDaysBefore || 0)} días antes` : 'Manual'}
                                </p>
                              </div>
                            </div>
                            {typeof onEditClient === 'function' ? (
                              <div className="mt-4 flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => onEditClient(row.client, row.contract)}
                                  className="inline-flex items-center gap-2 rounded-2xl border border-violet-300 bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(124,58,237,0.18)] transition hover:-translate-y-0.5 hover:bg-violet-700"
                                >
                                  {hasContract ? 'Editar acuerdo' : 'Configurar acuerdo'}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="overflow-hidden rounded-[24px] border border-slate-200 bg-white/90 shadow-sm">
                        <button
                          type="button"
                          onClick={() => toggleSection(row.clientId, 'history')}
                          className="flex w-full flex-wrap items-center justify-between gap-3 px-4 py-4 text-left"
                        >
                          <div>
                            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">Historial</p>
                            <p className="mt-1 text-sm font-bold text-slate-900">
                              {row.serviceCount} viaje{row.serviceCount === 1 ? '' : 's'} · {formatCurrency(row.estimatedCommercialTotal)} estimados
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-emerald-700">
                              {row.lastActivityAt ? `Último ${formatShortDateTime(row.lastActivityAt)}` : 'Sin viajes'}
                            </span>
                            <span className="text-slate-400">{isSectionOpen(row.clientId, 'history') ? '−' : '+'}</span>
                          </div>
                        </button>

                        {row.clientClosures.length > 0 ? (
                          <div className="border-t border-slate-200 px-4 py-4">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Cierres del cliente</p>
                            <div className="mt-3 space-y-2">
                              {row.clientClosures.slice(0, 3).map((closure) => (
                                <div key={closure.id} className="flex flex-wrap items-center justify-between gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                                  <div>
                                    <p className="font-bold text-slate-900">{closure.periodLabel || 'Cierre acumulado'}</p>
                                    <p className="mt-1 text-xs text-slate-500">
                                      {closure.tripCount || 0} viaje{closure.tripCount === 1 ? '' : 's'} · {closure.billingCadence === 'weekly' ? 'Semanal' : 'Mensual'}
                                    </p>
                                  </div>
                                  <div className="text-right">
                                    <span className={`rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ${
                                      String(closure?.status || '').trim().toLowerCase() === 'paid'
                                        ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                        : 'border-amber-200 bg-amber-50 text-amber-700'
                                    }`}>
                                      {resolveClosureStatusLabel(closure?.status)}
                                    </span>
                                    <p className="mt-2 text-xs font-semibold text-slate-600">{formatCurrency(closure?.saleTotalPending || 0)}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}

                        {isSectionOpen(row.clientId, 'history') ? (
                          row.monthServices.length > 0 ? (
                            <div className="border-t border-slate-200">
                              <div className="grid grid-cols-[120px_1.4fr_1fr_110px_110px_120px] gap-3 bg-slate-100 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                <span>Fecha</span>
                                <span>Recorrido</span>
                                <span>Modo</span>
                                <span className="text-right">Costo</span>
                                <span className="text-right">Diferencia</span>
                                <span className="text-right">Monto</span>
                              </div>
                              <div className="divide-y divide-slate-200">
                                {row.monthServices.slice(0, 8).map((service) => {
                                  const recognitionBadge = resolveServiceRecognitionBadge(service);
                                  return (
                                  <div key={service.id} className="grid grid-cols-[120px_1.4fr_1fr_110px_110px_120px] gap-3 px-4 py-3 text-sm text-slate-700">
                                    <span>{formatShortDateTime(serviceDate(service))}</span>
                                    <span className="font-medium text-slate-900">
                                      {service.origin || 'Sin origen'} → {service.destination || 'Sin destino'}
                                    </span>
                                    <span>
                                      {service.serviceTransportLabel || service.serviceTypeLabel || 'Servicio'}
                                      {service.commercialModelLabel ? ` · ${service.commercialModelLabel}` : ''}
                                      <span className={`mt-1 inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] ${recognitionBadge.tone}`}>
                                        {recognitionBadge.label}
                                      </span>
                                    </span>
                                    <span className="text-right font-semibold text-slate-700">
                                      {formatCurrency(service.realizedOperationalCost ?? service.estimatedOperationalCost ?? 0)}
                                    </span>
                                    <span className="text-right font-semibold text-cyan-700">
                                      {row.monthlyFixed > 0 ? 'Acumula' : formatCurrency(service.recognizedMargin ?? service.realizedMargin ?? service.estimatedMargin ?? 0)}
                                    </span>
                                    <span className="text-right font-bold text-slate-900">
                                      {row.monthlyFixed > 0 ? 'Abono' : formatCurrency(service.recognizedSalePrice ?? service.realizedSalePrice ?? service.salePrice ?? 0)}
                                    </span>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ) : (
                            <div className="border-t border-slate-200 px-4 py-4 text-sm text-slate-500">
                              Este cliente todavía no tiene viajes registrados en el período actual, pero ya puede quedar listo para usar.
                            </div>
                          )
                        ) : null}
                      </div>
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="rounded-[24px] border border-dashed border-slate-300 bg-slate-50/70 px-6 py-8 text-center">
          <p className="text-base font-bold text-slate-900">{search ? 'No encontramos clientes con esa búsqueda' : 'Todavía no hay clientes listos'}</p>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            {search
              ? 'Probá con otro nombre, teléfono o contacto para encontrar el cliente.'
              : 'Cuando empecemos a vender servicios de forma recurrente, acá vamos a ver la base comercial completa con su historial.'}
          </p>
        </div>
      )}
    </div>
  );
}
